import { FC, MouseEvent } from 'react';

import DefaultLayout from '@/layouts/Default/default';
import { NexusHero } from '@/components/Hero/NexusHero';
import {
  Paragraph,
  ImpactStatement,
  Heading2,
  Heading3,
  KeyBulletList,
  StyledCallout,
  ProcessStep,
  Quote,
  Divider,
} from '@/components/Blog/BlogComponents';
import { Seo } from '@/components/SEO/Seo';
import { pageMetadata } from '@/config/pageMetadata';

const foundationPillars = [
  {
    icon: 'psychology',
    title: 'Production RAG Pipelines',
    text: 'Versioned embeddings, eval harnesses, retention rules. Answers stay accurate after week one instead of drifting into fiction.',
  },
  {
    icon: 'visibility',
    title: 'OCR & Vision',
    text: 'PDFs, scans, whiteboards, site photos—agents read it all so humans stop copying data by hand.',
  },
  {
    icon: 'swap_horiz',
    title: 'Provider Switching',
    text: 'Cost and quality change weekly. We benchmark every task and swap models automatically based on signal.',
  },
  {
    icon: 'dns',
    title: 'Model Context Protocol',
    text: 'Bridge servers that stream tools, permissions, and live data into the model loop without duct tape.',
  },
];

const reasoningPillars = [
  {
    icon: 'account_tree',
    title: 'Reasoning Chains',
    text: 'Pocket-flow playbooks, tool hierarchies, and evaluation gates keep multi-step thinking on rails.',
  },
  {
    icon: 'diversity_3',
    title: 'Agent-to-Agent Messaging',
    text: 'Specialists coordinate, audit, and escalate work to each other instead of spamming humans.',
  },
  {
    icon: 'slideshow',
    title: 'Multimodal Surfaces',
    text: 'Agents ingest files, voice, CAD, telemetry—and return outputs leaders actually want, from spreadsheets to slides.',
  },
  {
    icon: 'insights',
    title: 'Narrative Delivery',
    text: 'Narrative-grade reporting, storyboards, and change logs so decisions happen instantly, not at the next standing meeting.',
  },
];

const operationsPillars = [
  {
    icon: 'ads_click',
    title: 'RPA & Browser Control',
    text: 'Playwright-driven DOM capture and replay so legacy tools fall in line without engineering babysitters.',
  },
  {
    icon: 'chat',
    title: 'Chat & Decision Trees',
    text: 'Chase-level craft on conversational flows that route intent, inject context, and know when to hand off to humans.',
  },
  {
    icon: 'build_circle',
    title: '3D Printer of Agents',
    text: 'Agents that test, grade, and tune other agents—continuous integration for intelligence.',
  },
  {
    icon: 'savings',
    title: 'Cost Curve Arbitrage',
    text: 'As software costs implode we multiply the spread—integrations, sites, ops, QA all land at a fraction of last year’s price.',
  },
];

const ctaCards = [
  {
    title: 'See the agent delivery indexes',
    description: 'Dive into Work and review how we shipped production agents for Gravitate, Narrative, and more.',
    href: '/work',
    image: '/case-studies/case-study.png',
    cta: 'Browse recent work',
  },
  {
    title: 'Read “The AI Gold Rush Explained”',
    description: 'Our perspective on why cost curves are collapsing and how the winners will compound advantage.',
    href: '/blog/the-ai-gold-rush-multiplying-business-capabilities',
    image: 'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=1400&q=80',
    cta: 'Read the essay',
  },
  {
    title: 'Talk through your first agent sprint',
    description: '15-minute fit check. We map the workflow, cost, and success metrics before anyone writes code.',
    href: '/contact',
    image: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=1400&q=80',
    cta: 'Book a fit check',
  },
];

const NexusPage: FC = () => {
  const seo = pageMetadata.nexus;
  const handleCardClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('/contact')) return;
    event.preventDefault();
    // @ts-expect-error custom global event handled by layout drawer
    window.dispatchEvent(new Event('open-contact'));
  };
  return (
    <DefaultLayout hideHeader>
      <Seo {...seo} />
      <NexusHero />

      <div className="min-h-screen w-full bg-white dark:bg-zinc-950">
        <div className="mx-auto w-full max-w-4xl px-4 py-24 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <Paragraph>
              The market is loud, timelines are insane, and most teams are stuck in slideware land. Still—the gold is real. The
              companies that learn how to put agents in production today will own the spread for the next decade.
            </Paragraph>

            <ImpactStatement content="In the next two years there will be obvious winners and obvious casualties. The gap will be unbridgeable." />

            <StyledCallout
              text="Where we stand"
              accentText="We’re the crew mining in fertile ground."
              lines={[
                'Two years of daily agent deployments across ops, product, and GTM.',
                'Component library + agent registry so new surfaces ship in hours, not weeks.',
                'Pods that treat AI like product engineering, not experiments.',
              ]}
            />

            <Divider />

            <Heading2 id="cycle" description="Why every company tells us they “tried AI” and gave up">
              The Vicious Cycle We Walk Into
            </Heading2>

            <div className="space-y-6">
              <ProcessStep number={1} title="AI is POWERFUL!!" description="Everyone gets sold the dream deck." />
              <ProcessStep
                number={2}
                title="Weeks of vendor safari"
                description="Teams evaluate generic tools that promise everything and solve nothing."
              />
              <ProcessStep
                number={3}
                title="Half-baked rollouts"
                description="Off-the-shelf platforms cover 20% of the workflow and shatter the rest." />
              <ProcessStep
                number={4}
                title={'"AI isn\'t ready"'}
                description="Momentum dies. Meanwhile competitors quietly ship custom automation that actually works." />
            </div>

            <Quote content="Generic AI products will never fit your unique workflows. While you wait for the 'perfect' solution, competitors build custom automation that actually works." />

            <Heading2 id="why-hard" description="Why breaking the cycle is brutal if you’re not an AI company">
              It’s Hard Because You Don’t Have a Daily R&amp;D Crew
            </Heading2>

            <Paragraph>
              Building agent ecosystems is a day-in, day-out craft. Models change weekly. Tooling breaks. Guardrails drift.
              Unless you have a team living in the edge cases, you end up buying promises while the real leverage goes to someone
              else. That’s why we exist.
            </Paragraph>

            <StyledCallout
              text="What the market is telling us"
              accentText="We hear the same four themes on every call."
              lines={[
                'Revenue + cost reduction still get us in the room—but the win is sharpening the “how” so teams can focus on needle-moving work.',
                'Deep expertise companies have a small window to encode their craft and widen their moat.',
                'Skepticism is real. Delivering proof in production is the moat.',
                'The curve is exponential. First movers compound advantage while everyone else waits for “certainty.”',
              ]}
            />

            <Divider />

            <Heading2 id="toolkit" description="Implementation scars we now deploy on day one">
              The Toolkit That Actually Works
            </Heading2>

            <Heading3 id="foundation" description="The infrastructure that keeps agents reliable">
              Foundation Patterns
            </Heading3>
            <KeyBulletList heading="Systems that survive production" description={null} points={foundationPillars} />

            <Heading3 id="reasoning" description="How we teach agents to behave in the wild">
              Reasoning That Holds Up
            </Heading3>
            <KeyBulletList heading="Brains with discipline" description={null} points={reasoningPillars} />

            <Heading3 id="operations" description="Keeping automation sharp once it ships">
              Operational Levers
            </Heading3>
            <KeyBulletList heading="Where the compounding happens" description={null} points={operationsPillars} />

            <Divider />

            <ImpactStatement content="Anything can be automated. In our hands a production agent averages $15–20K. After that it pays for itself on autopilot." />

            <StyledCallout
              text="Ready to grab the advantage?"
              accentText="Pick a workflow. We’ll ship the agent."
              lines={[
                'Run a live discovery—cost, friction, data, stakeholders—in 90 minutes.',
                'Plug into our component library and agent registry so you start with working pieces.',
                'Launch with dashboards, human-in-the-loop controls, and ROI instrumentation.',
                'Stay on the front line with a crew that treats AI like product, not theatre.',
              ]}
            />

            <Heading2 description="Receipts, frameworks, and the fastest way to start working with us.">
              Keep exploring
            </Heading2>

            <div className="space-y-6">
              {ctaCards.map((card) => (
                <a
                  key={card.title}
                  href={card.href}
                  onClick={(event) => handleCardClick(event, card.href)}
                  className="group flex flex-col overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-2 hover:shadow-md dark:border-zinc-700 dark:bg-zinc-900"
                >
                  <div className="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 space-y-4">
                      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-zinc-500 dark:text-zinc-400">
                        Featured
                      </span>
                      <h3 className="text-3xl font-light leading-snug text-zinc-900 dark:text-zinc-50">
                        {card.title}
                      </h3>
                      <p className="max-w-xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
                        {card.description}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-light text-blue-600 transition group-hover:translate-x-1 dark:text-blue-300">
                        {card.cta}
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 7h7v7" />
                        </svg>
                      </span>
                    </div>
                    <div className="relative h-48 w-full overflow-hidden rounded-2xl md:h-40 md:w-72">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default NexusPage;
